/**
 * The no-pricing rule for organization-authored text (spec 2026-10-15 §4.3).
 *
 * Pricing-aware, not a ban on digits: "Agent 360" and "Training 3 days a week"
 * pass; "$99/month", "250 per transaction" and "70/30 split" fail. A value is
 * pricing when it carries a currency or percent sign or word, or a number
 * within three words of a money word or deal phrase (fee, split, cap, "per
 * transaction"...), or right beside a frequency word or phrase (monthly,
 * "a month"...). "24/7" and "1:1" are not amounts.
 *
 * Lives under supabase/functions/_shared so Edge Functions can use it and so
 * PR 3's SQL mirror is checked against the same fixtures
 * (noPricing.fixtures.ts). Plain TypeScript: no Deno globals.
 */

const CURRENCY_OR_PERCENT = /[$€£%]|\b(?:percent|dollars?|bucks)\b/i;

// Money words and deal phrases count anywhere within WINDOW words of a number.
const MONEY_WORDS = new Set([
  "fee", "fees", "split", "splits", "cap", "caps", "capped", "cost", "costs",
  "price", "priced", "pricing", "charge", "charged", "charges", "flat", "desk",
]);

const MONEY_PHRASES = [
  "per transaction", "per deal", "per side", "per closing", "a deal",
];

// Frequency words and phrases only count right beside the number: the word as
// the token just before or just after it, the phrase starting at the token
// just after it. "4 sessions a month" is a schedule; "99 a month" is a price.
const FREQUENCY_WORDS = new Set(["monthly", "annually", "annual", "yearly"]);

const FREQUENCY_PHRASES = [
  "per month", "per mo", "per year", "per yr", "a month", "a year",
];

// "395/mo" and "395 / month" read as "395 per mo" / "395 per month".
const SLASH_UNIT = /(\d)\s*\/\s*(mo|month|yr|year)\b/gi;

// 12, 1.5, 1,500, 20k, 70/30
const NUMBER_TOKEN = /^(?:\d+(?:[.,]\d+)*k?|\d+\/\d+)$/;

// How many words either side of a number count as "next to" it. Written
// amounts ("five dollars", "ten percent") are caught by the currency words.
const WINDOW = 3;

// "24/7" reads as "always" and "1:1" as "one-on-one"; neither is an amount.
const ALWAYS = /\b24\s*\/\s*7\b/g;
const ONE_ON_ONE = /\b1\s*:\s*1\b/g;

export function looksLikePricing(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const text = value.trim();
  if (!text) return false;
  if (CURRENCY_OR_PERCENT.test(text)) return true;

  const tokens = text
    .toLowerCase()
    .replace(ALWAYS, "always")
    .replace(ONE_ON_ONE, "one-on-one")
    .replace(SLASH_UNIT, "$1 per $2")
    .split(/[^a-z0-9.,/]+/)
    .map((t) => t.replace(/^[.,/]+|[.,/]+$/g, ""))
    .filter(Boolean);

  for (let i = 0; i < tokens.length; i++) {
    if (!NUMBER_TOKEN.test(tokens[i])) continue;
    const window = tokens.slice(Math.max(0, i - WINDOW), i + WINDOW + 1);
    if (window.some((t) => MONEY_WORDS.has(t))) return true;
    const joined = ` ${window.join(" ")} `;
    if (MONEY_PHRASES.some((p) => joined.includes(` ${p} `))) return true;

    if (FREQUENCY_WORDS.has(tokens[i - 1]) || FREQUENCY_WORDS.has(tokens[i + 1])) return true;
    const after = ` ${tokens.slice(i + 1, i + 3).join(" ")} `;
    if (FREQUENCY_PHRASES.some((p) => after.startsWith(` ${p} `))) return true;
  }
  return false;
}
